import { BodyM, Button, Caption, Description } from '@casavo/base-ui'
import cn from 'classnames'
import { motion } from 'framer-motion'
import useTranslation from 'next-translate/useTranslation'
import { useEffect } from 'react'

import Icon from '@/components/Icon'
import { Translated } from '@/components/Translated'
import { useRelatedAdsBanner } from '@/context/RelatedAdsBannerProvider'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import { handleClick, handleShow } from '@/utils/tracking/eventsHandlers'

import { relatedAdsBannerImgCounter, relatedAdsBanner, relatedAdsBannerImg, relatedAdsBannerCTA } from './style.css'

type RelatedAdsBannerTouchpoints = 'FromPage' | 'FromCarousel' | 'FromPopup'
type Props = {
  className?: string
  onClickCallback?: () => void
  touchPoint: RelatedAdsBannerTouchpoints
}

export const RelatedAdsBanner: React.FC<Props> = ({ className, onClickCallback, touchPoint }) => {
  const { handleUserClickOnBanner, similarPropertiesPhotosUrl, similarPropertiesRef } = useRelatedAdsBanner()
  const handleBannerClick = () => {
    handleClick(GTMEventCategory.RelatedAdsBanner, 'ClickBanner', touchPoint)
    if (onClickCallback) {
      onClickCallback()
    }

    const delay = onClickCallback ? 1000 : 0
    setTimeout(() => similarPropertiesRef?.current.scrollIntoView({ behavior: 'smooth' }), delay)
    handleUserClickOnBanner()
  }

  useEffect(() => {
    handleShow(GTMEventCategory.RelatedAdsBanner, 'SeeBanner', touchPoint)
  }, [])

  const photos = similarPropertiesPhotosUrl
  const { t } = useTranslation()
  return (
    <motion.div
      animate={{ opacity: 1 }}
      className={cn(relatedAdsBanner, className)}
      data-testid="similar-property-banner"
      exit={{ opacity: 0 }}
      initial={{ opacity: 0 }}
      role="button"
      tabIndex={0}
      onClick={handleBannerClick}
    >
      <BodyM strong className={sprinkles({ paddingBottom: 'm' })}>
        {t('listing-detail:RelatedAdsBanner_Title', {
          count: similarPropertiesPhotosUrl.length,
          n: similarPropertiesPhotosUrl.length,
        })}
      </BodyM>

      <div>
        <div className={sprinkles({ display: 'flex', justifyContent: 'flex-end' })}>
          {photos.length > 0 && (
            <div className={sprinkles({ display: 'flex', justifyContent: 'flex-end' })}>
              {photos.slice(0, 3).map((photo) => (
                <img
                  key={photo}
                  alt=""
                  className={relatedAdsBannerImg}
                  data-testid="related-ads-banner-img"
                  height={32}
                  src={photo}
                  width={32}
                />
              ))}
            </div>
          )}
          {photos.length > 3 && (
            <div className={relatedAdsBannerImgCounter} data-testid="related-ads-banner-counter">
              <Description strong color="white">{`+${photos.length - 3}`}</Description>
            </div>
          )}
        </div>
      </div>

      <Caption className={sprinkles({ alignSelf: 'center' })}>
        <Translated i18Key="listing-detail:RelatedAdsBanner_Description" />
      </Caption>

      <Button
        iconRight
        className={relatedAdsBannerCTA}
        Icon={() => <Icon.Arrow direction="bottom" />}
        label={t('listing-detail:RelatedAdsBanner_CTA')}
        sizeVariant="small"
        styleVariant="tertiary"
      />
    </motion.div>
  )
}
